<script setup lang="ts">
import { useSlots } from "vue"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import GlowIcon from "@/components/af/GlowIcon/GlowIcon.vue"

import { storeToRefs } from "pinia"

import { useI18nStore } from "@/stores/i18n"

import { cn } from "@/lib/utils"

type Emits = {
  onSubmit: []
  onCancel: []
}
export interface Props {
  /**
   * Icon name to display on the modal (Font Awesome format)
   * @example "fas fa-circle-info"
   */
  iconName?: string
  title: string
  body: string
  cancelBtnText?: string
  submitBtnText?: string
  closable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  closable: true,
})
const emit = defineEmits<Emits>()

const slots = useSlots()
const { currentLocale } = storeToRefs(useI18nStore())

const isModalOpen = defineModel<boolean>("isModalOpen", { default: false })
const isModalSubmitDisabled = defineModel<boolean>("isModalSubmitDisabled", {
  default: false,
})
</script>

<template>
  <Dialog v-model:open="isModalOpen">
    <DialogContent
      :closable="props.closable"
      :class="
        cn(
          'border-afbranding-border bg-white lg:max-w-xl',
          `${currentLocale.iso_code_639 === 'ar-SA' ? 'arabic' : ''}` // Apply Arabic styles conditionally
        )
      "
    >
      <GlowIcon v-if="props.iconName" class="mx-auto" :icon="props.iconName" />

      <DialogTitle class="text-center text-afbranding-heading">
        {{ props.title }}
      </DialogTitle>

      <div class="mx-auto w-48">
        <Separator class="bg-grey-lighten-3" />
      </div>

      <DialogDescription>
        <slot name="body">
          <!-- Fallback to plain text -->
          <p class="text-center font-light text-afbranding-text">
            {{ props.body }}
          </p>
        </slot>
        <div v-if="slots.bottom" class="mt-4">
          <slot name="bottom" />
        </div>
      </DialogDescription>

      <div
        class="-mx-5 flex flex-col items-center justify-center gap-4 px-5 lg:m-0 lg:flex-row lg:px-0"
      >
        <Separator class="bg-grey-lighten-3 lg:hidden" />
        <div class="flex w-full flex-col-reverse gap-4 md:w-auto md:flex-row">
          <DialogClose as-child>
            <Button
              v-if="props.cancelBtnText"
              variant="secondary"
              @click="emit('onCancel')"
              >{{ props.cancelBtnText }}</Button
            >
          </DialogClose>
          <Button
            v-if="props.submitBtnText"
            :disabled="isModalSubmitDisabled"
            @click="emit('onSubmit')"
          >
            {{ props.submitBtnText }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
